import { Pipe, PipeTransform, ChangeDetectorRef } from '@angular/core';
import { GlobalTranslateService } from '../../services/globalTranslate/globalTranslate.service';
import { TranslateService } from "@ngx-translate/core";
import { ServiceConfigService } from '@app/services/service-config.service';

@Pipe({
  name: 'htmlTranslate',
  pure: false,
  standalone: false
})
export class HtmlTranslatePipe implements PipeTransform {
  private lastHtml: string = '';
  private lastLang: string = '';
  private finalValue: string = '';
  private isTranslating: boolean = false;
  awsTranslateSwitch = false;

  constructor(
    private globalTranslationService: GlobalTranslateService,
    public translateService: TranslateService,
    private cdr: ChangeDetectorRef,
    private serverConfig: ServiceConfigService
  ) {}

  transform(html: string): string {
    if (!html) {
      return '';
    }

    let selectedLang = sessionStorage.getItem('globalTranslatedLan') && 
                       sessionStorage.getItem('globalTranslatedLan') !== 'undefined' 
                       ? sessionStorage.getItem('globalTranslatedLan') 
                       : null;

    // Determine which language to use
    if (selectedLang === 'es') {
      selectedLang = 'sp';
    }
    
    const langToUse = (selectedLang === 'sp' || selectedLang === 'en') ? selectedLang : 'en';

    // Check if we need to translate
    if (html === this.lastHtml && langToUse === this.lastLang) {
      return this.finalValue;
    }

    this.lastHtml = html;
    this.lastLang = langToUse;
    this.awsTranslateSwitch = this.serverConfig.getServerConfig().awsTranslateSwitch;

    // For English or when AWS translate is off, return original HTML
    if (!this.awsTranslateSwitch || !selectedLang || selectedLang === 'en') {
      this.finalValue = html;
      return this.finalValue;
    }

    // For Spanish, use ngx-translate for known keys only
    if (selectedLang === 'sp') {
      this.translateHtmlWithNgxTranslate(html).then(translated => {
        this.finalValue = translated;
        this.cdr.markForCheck();
      });
      return this.finalValue || html;
    }

    // For other languages (AWS Translate)
    if (!this.isTranslating) {
      this.isTranslating = true;
      this.translateHtmlWithAws(html, selectedLang).then(translated => {
        this.finalValue = translated;
        this.isTranslating = false;
        this.cdr.markForCheck();
      });
    }

    return this.finalValue || html;
  }

  private async translateHtmlWithNgxTranslate(html: string): Promise<string> {
    // Parse HTML and extract text nodes with translation keys
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    // Find all text nodes that might be translation keys
    const walker = document.createTreeWalker(
      doc.body,
      NodeFilter.SHOW_TEXT,
      null
    );

    const textNodes: { node: Text; originalText: string }[] = [];
    let node: Node | null;

    while (node = walker.nextNode()) {
      const text = node.textContent?.trim();
      if (text) {
        textNodes.push({ node: node as Text, originalText: text });
      }
    }

    // Translate each text node
    for (const { node, originalText } of textNodes) {
      const translated = await this.translateService.get(originalText).toPromise();
      if (translated && translated !== originalText) {
        node.textContent = translated;
      }
    }

    return doc.body.innerHTML;
  }

  private async translateHtmlWithAws(html: string, targetLang: string): Promise<string> {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Extract all text nodes
    const walker = document.createTreeWalker(
      doc.body,
      NodeFilter.SHOW_TEXT,
      null
    );

    const textNodes: { node: Text; originalText: string }[] = [];
    let node: Node | null;

    while (node = walker.nextNode()) {
      const text = node.textContent?.trim();
      if (text && text.length > 0) {
        // Skip text that looks like it's just whitespace or special characters
        if (/[a-zA-Z]/.test(text)) {
          textNodes.push({ node: node as Text, originalText: text });
        }
      }
    }

    // Batch translate text nodes (AWS Translate has limits, so we'll do in chunks)
    const batchSize = 10;
    for (let i = 0; i < textNodes.length; i += batchSize) {
      const batch = textNodes.slice(i, i + batchSize);
      
      // Translate each text node in the batch
      await Promise.all(
        batch.map(async ({ node, originalText }) => {
          try {
            // First check if it's a translation key
            const ngxTranslated = await this.translateService.get(originalText).toPromise();
            const textToTranslate = (ngxTranslated && ngxTranslated !== originalText) ? ngxTranslated : originalText;
            
            // Then use AWS translate
            const translated = await this.globalTranslationService.translate(textToTranslate, targetLang);
            if (translated && translated !== 'Translation failed') {
              node.textContent = translated;
            }
          } catch (error) {
            console.error('Error translating text node:', error);
          }
        })
      );
    }

    return doc.body.innerHTML;
  }
}
